/**
 * ActiveProjectsCard — every active project on the dashboard, with the money
 * columns people actually chase and the same traffic light the Projects tab
 * shows.
 *
 * It replaces a card that was scoped to FANUC and listed Project / Customer /
 * Engineer / Code / Status — none of which answered "what is left to invoice".
 *
 * Deliberately the same interactions as the Projects tab, because a table that
 * looks like that one and behaves differently is worse than no table: click a
 * row to open the project, right-click to add a task, a comment, or change the
 * flag. The flag tint runs across the whole row.
 *
 * Any figure the data cannot supply reads "Pending" rather than a blank or a
 * zero — a missing margin and a 0% margin are not the same thing.
 */
import { fmt, fmtMoney } from '../../utils/format';
import { HEALTH, healthOf, rowTint, rowHover } from '../../data/projectHealth';
import { leftoverOf } from '../../utils/projectMoney';
import { computeMargin } from '../../utils/projectMoney';

/* Readable, not shouting: the old #B0B0B0 italic was ~2:1 on the row tint and
   filled whole columns with slanted grey. Same word, calmer. */
const PENDING = <span style={{ color: '#6B7168', fontSize: 10, fontWeight: 500 }}>Pending</span>;

/* Headers wrap ("As sold / margin") and sit on the row's bottom edge; each has
   a width so the table fits in HALF the dashboard (r34: 1,622px -> ~800px).
   Sticky with an opaque background, because the body scrolls under it. */
const TH = {
  padding: '8px',
  textAlign: 'left',
  fontSize: 9,
  fontWeight: 700,
  color: '#5A5A5A',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  background: '#FAFBFC',
  whiteSpace: 'normal',
  verticalAlign: 'bottom',
  lineHeight: 1.2,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  boxShadow: 'inset 0 -1px 0 #E6E9E3',
};
const TD = { padding: '7px 8px', fontSize: 11, color: '#1a1a1a', borderBottom: '1px solid #F2F4F0', verticalAlign: 'top' };
const NUM = { ...TD, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const NEG = { color: '#B71C1C', fontWeight: 600 };
const CLAMP2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? fmt(n) : null;
};

/** Margin actually sold: (PO value - quoted COGS) / PO value. */
function asSoldMargin(p) {
  const po = Number(p.poValue ?? p.po_value);
  const cogs = (Number(p.quotedLaborCost ?? p.quoted_labor_cost) || 0) + (Number(p.quotedExpenses ?? p.quoted_expenses) || 0);
  if (!Number.isFinite(po) || po <= 0 || !cogs) return null;
  return (po - cogs) / po;
}

// two decimals, like every other percentage in the app
const pct = (m) => (m == null ? null : `${(m * 100).toFixed(2)}%`);

// `fill`: take the height of the box it sits in (the Dashboard widget) and
// scroll the rows inside it, instead of the fixed 560px pane.
export default function ActiveProjectsCard({ projects, onOpen, onContext, onViewAll, fill = false }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #ECECEC',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...(fill ? { height: '100%', boxSizing: 'border-box' } : null),
      }}
    >
      <div
        style={{
          padding: '16px 22px',
          borderBottom: '1px solid #F0F0F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0D0D0D' }}>Active Projects ({projects.length})</span>
          <div style={{ fontSize: 10, color: '#5A5A5A', marginTop: 2 }}>
            Click a row to open it · right-click to add a task, a comment, or change its flag
          </div>
        </div>
        <span
          role="button"
          tabIndex={0}
          className="go-focus"
          onClick={onViewAll}
          onKeyDown={(e) => e.key === 'Enter' && onViewAll()}
          style={{ fontSize: 11, color: '#1B5E20', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          View All
        </span>
      </div>

      {/* A capped pane: 94 rows uncapped would run ~4,300px beside a short
          task list. Red then yellow sort to the top, so they are always in the
          first screenful; the rest scroll under the sticky header. */}
      <div style={{ overflow: 'auto', ...(fill ? { flex: 1, minHeight: 0 } : { maxHeight: 560 }) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {/* 108: a flagged row's dot + gap take 13px, and "FANUC - PSH"
                  split onto two lines at 92 */}
              <th style={{ ...TH, width: 108 }}>Customer</th>
              <th style={{ ...TH, width: 86 }}>Sub</th>
              <th style={TH}>Description</th>
              <th style={{ ...TH, width: 50 }}>Quote&nbsp;#</th>
              <th style={{ ...TH, width: 88, textAlign: 'right' }}>PO Value</th>
              <th style={{ ...TH, width: 70, textAlign: 'right' }}>As Sold Margin</th>
              <th style={{ ...TH, width: 70, textAlign: 'right' }}>Expected Margin</th>
              <th style={{ ...TH, width: 86, textAlign: 'right' }}>Invoiced</th>
              <th style={{ ...TH, width: 92, textAlign: 'right' }}>Left to Invoice</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr>
                <td style={{ ...TD, textAlign: 'center', color: '#bbb', padding: 28 }} colSpan={9}>
                  No active projects.
                </td>
              </tr>
            )}
            {projects.map((p, i) => {
              const h = healthOf(p);
              const soldM = asSoldMargin(p);
              const expectedM = computeMargin(p);
              const sold = pct(soldM);
              const expected = pct(expectedM);
              const invoiced = money(p.invoicedAlready ?? p.invoiced_already);
              const left = leftoverOf(p);
              const po = money(p.poValue ?? p.po_value);
              return (
                <tr
                  key={p.id}
                  data-health={h}
                  style={{ '--health-tint': rowTint(p, i), '--health-hover': rowHover(p), cursor: 'pointer' }}
                  onClick={() => onOpen(p)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onContext(p, e.clientX, e.clientY);
                  }}
                >
                  <td style={TD}>
                    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {h !== 'green' && (
                        <span
                          title={HEALTH[h].label}
                          style={{ width: 7, height: 7, borderRadius: '50%', background: HEALTH[h].dot, flexShrink: 0, marginTop: 4 }}
                        />
                      )}
                      <span style={{ fontWeight: 700 }}>{p.customer || PENDING}</span>
                    </span>
                  </td>
                  <td style={{ ...TD, color: '#5A5A5A' }}>{p.subCustomer || p.sub_customer || PENDING}</td>
                  {/* clamped on an inner div: -webkit-box on the <td> itself
                      stops it being a table cell and leaves a hole in the tint */}
                  <td style={TD} title={p.description || p.name || ''}>
                    <div style={CLAMP2}>{p.description || p.name || PENDING}</div>
                  </td>
                  <td style={{ ...TD, fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 10.5 }}>
                    {p.quoteLetter || p.quote_letter || PENDING}
                  </td>
                  <td style={NUM}>{po || PENDING}</td>
                  <td style={{ ...NUM, ...(soldM < 0 ? NEG : null) }}>{sold || PENDING}</td>
                  <td style={{ ...NUM, ...(expectedM < 0 ? NEG : null) }}>{expected || PENDING}</td>
                  <td style={NUM}>{invoiced || PENDING}</td>
                  <td style={{ ...NUM, fontWeight: 700, color: left > 0 ? '#92400E' : undefined }}>
                    {left == null ? PENDING : left < 0 ? <span style={NEG}>-{fmtMoney(-left)}</span> : fmt(left)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
